import { KeyboardEvent, useContext } from 'react';
import { NodeViewContent, NodeViewProps, NodeViewWrapper, useEditorState } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import { Caption1 } from '@fluentui/react-components';
import classNames from 'classnames';

import DocumentContext from '../../../contexts/DocumentContext.context';
import { TEMPLATE_FIELD_DROPDOWN_NAME } from '../../../doc-engine/templateFields';
import useDropdownOptions from '../../../hooks/useDropdownOptions.hook';
import DropdownChoicesPanel from '../templateField/DropdownChoicesPanel';
import { setDropdownOptions } from '../templateField/dropdownFieldUtils';
import {
  handleSettingsHistoryKeys,
  isUnlabelled,
  setFieldSettings,
  TFieldSettings,
} from '../templateField/fieldSettings';
import FieldTextSettings from '../templateField/FieldTextSettings';
import { FORM_FIELD_CLASS_NAME } from './constants';
import { duplicateFormField, getWrappedField } from './formFieldHelpers';
import FormFieldCardFooter from './FormFieldCardFooter';
import type { TFormFieldOptions } from './FormFieldNode';

// The field keeps taking input in place: what the author types is the answer the form starts with.
const FormFieldCard = ({ node, editor, getPos, deleteNode, extension }: NodeViewProps) => {
  const { copyFields } = extension.options as TFormFieldOptions;
  const { templateDropdownOptionsRef } = useContext(DocumentContext);
  const field = getWrappedField(node, 0)?.node;
  // The card is active while it holds the editor's selection, on itself or inside; its settings select it on focus.
  const isActive = useEditorState({
    editor,
    selector: ({ editor: current }) => {
      const pos = getPos();
      if (pos === undefined) return false;
      const { selection } = current.state;
      if (selection instanceof NodeSelection && selection.from === pos) return true;
      // Strictly inside: the edge two cards share belongs to neither.
      return selection.from > pos && selection.to < pos + node.nodeSize;
    },
  });
  const options = useDropdownOptions(field?.attrs.fieldId);
  // Only before cleanup has removed a card that lost its field.
  if (!field) return null;

  const fieldId: string = field.attrs.fieldId;

  // The settings belong to the field inside, as they do for a field in a paragraph.
  const updateSettings = (patch: Partial<TFieldSettings>) => {
    const pos = getPos();
    const wrapped = pos === undefined ? undefined : getWrappedField(node, pos);
    if (wrapped) setFieldSettings(editor, wrapped.pos, patch);
  };

  const duplicate = () => {
    const pos = getPos();
    if (pos !== undefined) duplicateFormField(editor, node, pos, copyFields);
  };

  // Focus stays in the setting: the editor does not own the DOM selection while an input holds the focus.
  const selectOnFocus = () => {
    const pos = getPos();
    if (pos === undefined || isActive) return;
    editor.view.dispatch(editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, pos)));
  };

  return (
    <NodeViewWrapper
      className={classNames(
        FORM_FIELD_CLASS_NAME,
        'my-3 rounded-md border bg-white transition-[border-color,box-shadow]',
        isActive
          ? 'border-templateField ring-[3px] ring-templateField/15'
          : 'border-neutralStroke-2 hover:border-neutral-400'
      )}
      onFocus={selectOnFocus}
      onKeyDown={(event: KeyboardEvent) => handleSettingsHistoryKeys(editor, event)}
    >
      <div contentEditable={false} className="flex flex-col gap-2 px-3.5 pt-3">
        {/* A missing label shows while the card is idle: completing the template asks for every label. */}
        <FieldTextSettings
          editor={editor}
          field={field}
          isLabelMarked={isUnlabelled(field.attrs.label) && !isActive}
          onSettingsChange={updateSettings}
        />
        {/* Text, not a Label: a press on a card's label never reaches the editor, so it would not select the card. */}
        <Caption1 className="py-0.5">Default value</Caption1>
      </div>
      <NodeViewContent className="px-3.5 pt-0.5" />
      <div contentEditable={false} className="px-3.5 pb-3.5">
        {field.type.name === TEMPLATE_FIELD_DROPDOWN_NAME && isActive && (
          <div className="pt-2">
            <DropdownChoicesPanel
              options={options ?? []}
              selectedValue={field.attrs.selectedValue}
              onChange={(choices) =>
                setDropdownOptions(editor, templateDropdownOptionsRef, fieldId, choices.options, choices.selectedValue)
              }
            />
          </div>
        )}
      </div>
      {isActive && (
        <FormFieldCardFooter
          required={field.attrs.required}
          onSettingsChange={updateSettings}
          onDuplicate={duplicate}
          onDelete={deleteNode}
        />
      )}
    </NodeViewWrapper>
  );
};

export default FormFieldCard;
